import { scanStructure } from '../support';
import type { RepairContext, RepairProposal, RepairRule } from '../types';

/** The two opening typographic quotes. Their closing partners live in `support.ts`. */
const SMART_OPENERS: ReadonlySet<string> = new Set(['“', '‘']);

/**
 * Re-emit a literal delimited by typographic quotes as a JSON string: `{“a”: 1}` → `{"a": 1}`.
 *
 * **Heuristic, and this is the rule that explains why the category exists** (ADR-0012). A
 * `“` is a legitimate character: in `{"q":"“hi”"}` it is a quotation inside a value, and
 * the legacy global `.replace(/[“”]/g, '"')` turned that valid document into
 * `{"q":""hi""}` — broken JSON produced from correct input (recon §3.3, A15).
 *
 * Being token-aware removes that failure completely: a smart quote inside a `String` token
 * is data and is never seen here. What remains genuinely undecidable is a smart quote in a
 * position where a delimiter would go — the user may have meant a delimiter, or may have
 * pasted prose. So the rule fires only there, and even there it needs explicit consent
 * rather than riding along with "Fix all".
 */
export const normalizeSmartQuotesRule: RepairRule = {
  id: 'normalize-smart-quotes',
  confidence: 'heuristic',
  priority: 32,
  resolves: ['string/smart-quoted'],

  detect(ctx: RepairContext): readonly RepairProposal[] {
    return scanStructure(ctx.source, ctx.tokens)
      .foreign.filter((literal) => SMART_OPENERS.has(literal.open))
      .map((literal) => ({
        ruleId: 'normalize-smart-quotes',
        span: { from: literal.from, to: literal.to },
        edits: [{ from: literal.from, to: literal.to, insert: JSON.stringify(literal.value) }],
        labelCode: 'fix.normalize-smart-quotes',
        resolves: 'string/smart-quoted',
      }));
  },
};
